import { Box, Button, Grid, Heading, Spinner, Text, VStack, HStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import http from '../../utils/http'

interface PilotAnalytics {
  totalSessions: number
  totalAssessments: number
  totalBookings: number
  postcodesBlocked: number
  grantEligibilityBreakdown: Record<string, number>
  conversionRates?: {
    sessionToAssessment: number
    assessmentToBooking: number
    overall: number
  }
  grantMetrics?: {
    totalPotentialValue: number
    eligibilityRate: number
  }
}

interface PilotAnalyticsPanelProps {
  councilSlug: string
}

const PilotAnalyticsPanel: React.FC<PilotAnalyticsPanelProps> = ({ councilSlug }) => {
  const navigate = useNavigate()

  const { data, isLoading } = useQuery<PilotAnalytics>({
    queryKey: ['pilotAnalytics', councilSlug],
    queryFn: async () => {
      const res = await http.get(`/api/pilot/${councilSlug}/analytics/`)
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })

  return (
    <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="xl" p={{ base: 4, md: 6 }}>
      <VStack align="stretch" gap={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ base: 'stretch', sm: 'center' }}
          flexDirection={{ base: 'column', sm: 'row' }}
          gap={3}
        >
          <Heading size="md" color="blue.800">Pilot Widget Analytics</Heading>
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            onClick={() => navigate(`/pilot/${councilSlug}/dashboard`)}
            alignSelf={{ base: 'stretch', sm: 'auto' }}
          >
            Full dashboard →
          </Button>
        </Box>

        {isLoading ? (
          <Spinner size="sm" />
        ) : data ? (
          <VStack align="stretch" gap={3}>
            <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={3}>
              {[
                { label: 'Sessions', value: data.totalSessions },
                { label: 'Assessments', value: data.totalAssessments },
                { label: 'Bookings', value: data.totalBookings },
                { label: 'Out-of-area', value: data.postcodesBlocked },
              ].map(({ label, value }) => (
                <Box key={label} bg="white" borderRadius="lg" p={3} border="1px solid" borderColor="blue.100">
                  <Text fontSize="xs" color="gray.500" mb={1}>{label}</Text>
                  <Text fontSize="xl" fontWeight="bold" color="blue.800">{value}</Text>
                </Box>
              ))}
            </Grid>
            {(data.conversionRates || data.grantMetrics) && (
              <HStack gap={4} pt={2} borderTop="1px solid" borderColor="blue.100" flexWrap="wrap">
                {data.conversionRates && (
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold" color="green.600">{data.conversionRates.overall}%</Text> overall conversion
                  </Text>
                )}
                {data.grantMetrics && (
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="semibold" color="blue.600">£{data.grantMetrics.totalPotentialValue.toLocaleString()}</Text> grant value identified
                  </Text>
                )}
              </HStack>
            )}
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.500">No pilot data available yet.</Text>
        )}
      </VStack>
    </Box>
  )
}

export default PilotAnalyticsPanel
